import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { HdBirthTimeValue } from '../hd-chart-library.models';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { HdContentLanguageService } from '../../bodygraph/hd-content-language.service';

type TimeDisplay = '24h' | 'ampm';
type Meridiem = 'AM' | 'PM';

@Component({
  selector: 'hd-birth-time-input',
  standalone: true,
  imports: [SharedModule, TranslocoModule],
  templateUrl: './hd-birth-time-input.component.html',
  styleUrl: './hd-birth-time-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdBirthTimeInputComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Optionele begintijd (24u "HH:mm"). */
  readonly initialTime = input<string>('12:00');
  /** Optionele beginstand van de UTC-toggle (edit-modus). */
  readonly initialIsUtc = input(false);
  readonly valueChange = output<HdBirthTimeValue>();

  protected readonly time = signal('12:00');     // canonical 24u
  protected readonly display = signal<TimeDisplay>('24h');
  protected readonly isUtc = signal(false);

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      timeAria: translate('humanDesign.chart.createDialog.timeAria'),
      chooseTime: translate('humanDesign.chart.createDialog.chooseTime'),
      timeFormatHint: translate('humanDesign.chart.createDialog.timeFormatHint'),
      timeModeGroupAria: translate('humanDesign.chart.createDialog.timeModeGroupAria'),
      localTime: translate('humanDesign.chart.createDialog.localTime'),
      utc: translate('humanDesign.chart.createDialog.utc'),
      utcTooltip: translate('humanDesign.chart.createDialog.utcTooltip'),
      utcNote: translate('humanDesign.chart.createDialog.utcNote'),
    };
  });

  ngOnInit(): void {
    this.time.set(this.initialTime());
    this.isUtc.set(this.initialIsUtc());
  }

  /** Native <input type="time"> (24u) → canonical. */
  protected onNativeTimeInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value || '00:00';
    this.setTime(value);
  }

  /** Opent de native tijd-picker (Chrome/Edge); valt terug op focus als showPicker ontbreekt. */
  protected openPicker(input: HTMLInputElement): void {
    const withPicker = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof withPicker.showPicker === 'function') {
      try {
        withPicker.showPicker();
        return;
      } catch {
        // showPicker kan gooien buiten een user-gesture/onveilige context — val terug op focus.
      }
    }
    input.focus();
  }

  setTime(hhmm: string): void {
    this.time.set(hhmm);
    this.emit();
  }

  setUtcMode(isUtc: boolean): void {
    if (this.isUtc() === isUtc) {
      return;
    }
    this.isUtc.set(isUtc);
    this.emit();
  }

  setDisplay(display: TimeDisplay): void {
    this.display.set(display);
    this.emit();
  }

  /** AM/PM → 24u. 12 AM = 00, 12 PM = 12. */
  toTwentyFourHour(hour12: number, minutes: number, meridiem: Meridiem): string {
    let h = hour12 % 12;
    if (meridiem === 'PM') h += 12;
    return `${String(h).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private emit(): void {
    this.valueChange.emit({ time: this.time(), isUtc: this.isUtc(), display: this.display() });
  }
}
